import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { CollapsibleBox } from "@/components/CollapsibleBox";
import { buttonClass } from "@/components/buttonStyles";
import { SubmissionMessageComposer } from "@/components/bond-submission/SubmissionMessageComposer";
import type { BondAction } from "@/lib/bondStateModel";
import { formatShortDateTime } from "@/lib/listFormat";
import {
  listSubmissionMessages,
  messageAuthorLabel,
  messageTopicLabel,
  type SubmissionMessage,
} from "@/lib/submissionMessages";

// BRK-S425 — the submission page's Messages tab (the wireframe's "Messages"
// box): the order's thread with Signal surety, oldest first, and
// the composer. The thread is PMS's (PMS-S842) and is RE-READ
// after a send — here or from the header's dialog (`version`) — so a message
// is shown only once the server carries it (NN #7). Text renders as text:
// React escapes it, and nothing here parses markup.

type Thread =
  | { state: "loading" }
  | { state: "failed" }
  | { state: "read"; messages: SubmissionMessage[] };

export function SubmissionMessagesPanel({
  submissionId,
  message,
  version = 0,
  onSent,
}: {
  submissionId: string;
  /** The order view's `actions.message` (with the page's capability gate
   *  folded in): the composer shows only while it is enabled. */
  message: BondAction;
  /** Bumped by the host when the header's dialog has sent. */
  version?: number;
  /** After this tab's composer has sent (the host re-reads the order, whose
   *  History logs the message). */
  onSent?: () => void;
}) {
  const { authedRequest } = useAuth();
  const [thread, setThread] = useState<Thread>({ state: "loading" });
  const [reads, setReads] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listSubmissionMessages(authedRequest, submissionId)
      .then((messages) => {
        if (!cancelled) setThread({ state: "read", messages });
      })
      .catch(() => {
        // A failed re-read keeps the thread already on screen.
        if (!cancelled) {
          setThread((t) => (t.state === "read" ? t : { state: "failed" }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [authedRequest, submissionId, version, reads]);

  return (
    <CollapsibleBox title="Messages">
      <p className="text-sm text-brk-dim">
        With Signal surety · replies also come to your email
      </p>
      {thread.state === "loading" && (
        <p role="status" className="text-sm text-brk-dim">
          Loading messages…
        </p>
      )}
      {thread.state === "failed" && (
        <div className="flex flex-col items-start gap-2">
          <p role="alert" className="text-sm text-brk-danger">
            We couldn’t load the messages for this submission.
          </p>
          <button
            type="button"
            onClick={() => {
              setThread({ state: "loading" });
              setReads((n) => n + 1);
            }}
            className={buttonClass({ variant: "secondary", size: "sm" })}
          >
            Try again
          </button>
        </div>
      )}
      {thread.state === "read" &&
        (thread.messages.length === 0 ? (
          <p className="text-sm text-brk-dim">No messages yet</p>
        ) : (
          <ol aria-label="Messages" className="flex min-w-0 flex-col gap-3">
            {thread.messages.map((m) => (
              <MessageItem key={m.id} message={m} />
            ))}
          </ol>
        ))}
      {message.enabled ? (
        <SubmissionMessageComposer
          submissionId={submissionId}
          label="New message to Signal"
          note="Messages are also logged in History."
          onSent={() => {
            setReads((n) => n + 1);
            onSent?.();
          }}
        />
      ) : (
        <p className="text-sm text-brk-dim">
          {message.reason ??
            "Messaging Signal isn’t available on this submission."}
        </p>
      )}
    </CollapsibleBox>
  );
}

/** One message. Full width at 375px; from `sm` the broker's own sit to the
 *  right and Signal's to the left, as a conversation reads. */
function MessageItem({ message: m }: { message: SubmissionMessage }) {
  const mine = m.from === "broker";
  return (
    <li
      className={`flex min-w-0 flex-col gap-1.5 rounded-lg border p-3 text-sm sm:max-w-[85%] ${
        mine
          ? "border-brk-accent/30 bg-brk-accent/5 sm:self-end"
          : "border-brk-border bg-brk-panel sm:self-start"
      }`}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium text-brk-text">
          {messageAuthorLabel(m)}
        </span>
        <time dateTime={m.at} className="text-brk-dim">
          {formatShortDateTime(m.at)}
        </time>
        <span className="inline-flex items-center rounded-full bg-brk-bg px-2 py-0.5 text-xs font-medium text-brk-dim ring-1 ring-brk-border">
          {messageTopicLabel(m.topic)}
        </span>
      </div>
      <p className="whitespace-pre-wrap break-words text-brk-text">{m.body}</p>
    </li>
  );
}
